'use strict';
import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import urljoin from 'url-join';
import { useDispatch } from 'react-redux';
import {
	Box,
	Typography,
	Tabs,
	Tab,
	List,
	ListItem,
	ListItemText,
	IconButton,
	Collapse,
	Button,
	Paper,
	CircularProgress,
	Switch,
} from '@mui/material';
import { VariableSizeList } from 'react-window';
import {
	ChevronRight,
	ExpandMore,
	Add,
	Close,
	Download,
	ModeEditOutline
} from '@mui/icons-material';
import {
	useRolesQuery,
	useSearchRolesQuery,
	useUsersQuery,
	useManagedProjectsQuery,
	useSearchUsersQuery,
	useSearchProjectsQuery,
	useAddUserToProjectMutation,
	useAddUsersToProjectMutation,
	useAddProjectsToUserMutation,
	useAddRolesToUserMutation,
	useAddUserToRoleMutation,
	useAddUsersToRoleMutation,
	useAddUserMutation,
	useApproveUserSalesforceMutation,
	useRemoveUserFromProjectMutation,
	useRemoveUserFromRoleMutation,
	useRemoveRolesFromUserMutation,
	useLazyUserProjectsQuery,
	useLazyUserRolesQuery,
	useLazyUserLoginStatusQuery,
	useLazyUserTalentLmsSyncQuery,
	useReconcileUserTalentLmsMutation,
	useLazyProjectUsersQuery,
	useLazyRoleUsersQuery,
} from '@/redux/services/portal/admin.api';
import { getLoginStatusTypographyColor } from './loginStatus';
import { getTalentLmsSyncTypographyColor, formatTalentLmsBranchSummary } from './talentLmsSync';
import { clearLoginStatusEntry, normalizeLoginStatusEmailKey, shouldSkipLoginStatusFetch } from '../../../utils/loginStatusFetch';
import { clearTalentLmsSyncEntry, normalizeTalentLmsSyncEmailKey, shouldSkipTalentLmsSyncFetch } from '../../../utils/talentLmsSyncFetch';
import { getProblemDetailMessage } from '../../../utils/problemDetails';
import { portalApi } from '@/redux/services/portal/index.api';
import { SearchBox } from '@/components/index.jsx';
import { usePermissions } from '@/hooks/permissions.hook';
import * as permissions from '@/security/permissionTypes';
import Modal from '@/lenses/uilenses/components/Modal/Modal';
import { scrubHTML } from '@/utils/StringUtils';
import FileUtils from '@/utils/FileUtils';
import { getAuth0Token } from '@/utils/auth0Token';
import { API_BASEPATH_PORTAL } from '@/config/env.config';
import { toast } from 'react-toastify';
import AddNewUserModal from './AddNewUserModal';
import AddProjectToUserModal from './AddProjectToUserModal';
import AddRoleToUserModal from './AddRoleToUserModal';
import AddUserToProjectModal from './AddUserToProjectModal';
import AddUserToRoleModal from './AddUserToRoleModal';
import { useAccessSearch } from './useAccessSearch';
import { useAccessQueries } from './useAccessQueries';
import './AccessManagementContainer.scss';

/**
 * Configuration-driven tab definitions.
 * 
 * REFACTOR VALIDATION:
 * 1. Where would a new "Organization" tab be added?
 *    Answer: Add a new entry to the array returned by createAccessTabsConfig below.
 * 
 * 2. What code would need to change to support it?
 *    Answer: Only the array returned by createAccessTabsConfig. No rendering logic changes needed.
 * 
 * SEARCH STRATEGY:
 * Each tab defines a search configuration object with:
 * - mode: "client" | "server" - determines filtering strategy
 * - placeholder: string - search input placeholder text
 * - debounceMs: number - debounce delay in milliseconds (optional)
 * - minChars: number - minimum characters before search triggers (optional)
 * 
 * Currently all tabs use mode: "client" for client-side filtering.
 * When mode: "server" is implemented, search will trigger API calls via RTK Query.
 */
const createAccessTabsConfig = (handlers, dataSources, queryHooks) => {
	const {
		handleAddProject,
		handleRemoveProject,
		handleAddRole,
		handleRemoveRole,
		handleAddUserToProject,
		handleRemoveUserFromProject,
		handleAddUserToRole,
		handleRemoveUserFromRole,
		fetchUserProjects,
		fetchUserRoles,
		fetchProjectUsers,
		fetchRoleUsers,
	} = handlers;

	const { users, projects, roles } = dataSources;
	const { useAllQuery, useSearchQuery } = queryHooks;

	return [
		{
			key: 'users',
			label: 'Users',
			search: {
				mode: 'server',
				placeholder: 'Name or email',
				debounceMs: 400,
				minChars: 1,
			},
			emptyStateLabel: 'users',
			getData: () => [],
			filterPrincipal: (principal, searchLower) => 
				(principal.name || '').toLowerCase().includes(searchLower) ||
				(principal.email || '').toLowerCase().includes(searchLower),
			getPrincipalId: (principal) => principal.id || principal.email,
			getPrincipalPrimaryText: (principal) => principal.name || principal.email,
			getPrincipalSecondaryText: (principal) => principal.email,
			// Data fetching configuration
			useAllQuery: useAllQuery.useUsersQuery,
			useSearchQuery: useSearchQuery.useSearchUsersQuery,
			// Mapper function to transform DTO to frontend format
			mapItem: (item) => ({
				...item,
				id: item.email || item.Email,
				name: `${item.firstName || item.FirstName || ''} ${item.lastName || item.LastName || ''}`.trim() || item.email || item.Email,
				email: item.email || item.Email,
			}),
			scopes: [
				{
					key: 'projects',
					label: 'Projects',
					getItems: (principal) => principal.projects || [],
					addLabel: 'Add Project',
					onAdd: (principal) => handleAddProject(principal),
					onRemove: (principal, item) => handleRemoveProject(principal.id, item.projectId),
					getItemKey: (item) => item.projectId,
					getItemText: (item) => item.projectCode,
					getItemSecondaryText: () => null,
					fetchItems: (principal, skip, take) => fetchUserProjects(principal.email, skip, take),
				},
				{
					key: 'roles',
					label: 'Roles',
					getItems: (principal) => principal.roles || [],
					addLabel: 'Manage Roles',
					onAdd: (principal) => handleAddRole(principal),
					onRemove: (principal, item) => handleRemoveRole(principal.id, item.id || item.name),
					getItemKey: (item) => item.id || item.name,
					getItemText: (item) => item.name,
					getItemSecondaryText: () => null,
					fetchItems: (principal, skip, take) => fetchUserRoles(principal.email, skip, take),
				},
			],
		},
		{
			key: 'projects',
			label: 'Projects',
			search: {
				mode: 'server',
				placeholder: 'Project code or name',
				debounceMs: 400,
				minChars: 1,
			},
			searchDataModel: 'managedProjects',
			emptyStateLabel: 'projects',
			getData: () => projects,
			filterPrincipal: (principal, searchLower) =>
				(principal.code || '').toLowerCase().includes(searchLower) ||
				(principal.description || '').toLowerCase().includes(searchLower),
			getPrincipalId: (principal) => principal.id || principal.code,
			getPrincipalPrimaryText: (principal) => principal.code || principal.name,
			getPrincipalSecondaryText: (principal) => principal.description,
			// Data fetching configuration
			useAllQuery: useAllQuery.useManagedProjectsQuery,
			useSearchQuery: useSearchQuery.useSearchProjectsQuery,
			// Mapper function to transform DTO to frontend format
			mapItem: (item) => ({
				...item,
				id: item.projectId,
				code: item.projectCode,
				name: item.projectCode,
				description: item.projectTitle
			}),
			scopes: [
				{
					key: 'users',
					label: 'Users',
					getItems: (principal) => principal.users || [],
					addLabel: 'Add User',
					onAdd: (principal) => handleAddUserToProject(principal),
					onRemove: (principal, item) => handleRemoveUserFromProject(principal.id || principal.projectId, item),
					getItemKey: (item) => item.id || item.email,
					getItemText: (item) => item.name || item.email,
					getItemSecondaryText: (item) => item.email,
					fetchItems: (principal, skip, take) => fetchProjectUsers(principal.id || principal.projectId, skip, take),
				},
			],
		},
		{
			key: 'roles',
			label: 'Roles',
			search: {
				mode: 'server',
				placeholder: 'Role name',
				debounceMs: 400,
				minChars: 1,
			},
			emptyStateLabel: 'roles',
			getData: () => roles,
			filterPrincipal: (principal, searchLower) =>
				(principal.name || '').toLowerCase().includes(searchLower) ||
				(principal.description || '').toLowerCase().includes(searchLower),
			getPrincipalId: (principal) => principal.name,
			getPrincipalPrimaryText: (principal) => principal.name,
			getPrincipalSecondaryText: (principal) => principal.description,
			// Data fetching configuration
			useAllQuery: useAllQuery.useRolesQuery,
			useSearchQuery: useSearchQuery.useSearchRolesQuery,
			// Mapper function to transform DTO to frontend format
			mapItem: (item) => ({
				...item,
				id: item.name,
				name: item.name,
				description: item.description
			}),
			scopes: [
				{
					key: 'users',
					label: 'Users',
					getItems: (principal) => principal.users || [],
					addLabel: 'Add User',
					onAdd: (principal) => handleAddUserToRole(principal),
					onRemove: (principal, item) => handleRemoveUserFromRole(principal.id, item.id),
					getItemKey: (item) => item.id || item.email,
					getItemText: (item) => item.name || item.email,
					getItemSecondaryText: (item) => item.email,
					fetchItems: (principal, skip, take) => fetchRoleUsers(principal.name, skip, take),
				},
			],
		},
	];
};

const PAGE_SIZE = 20;
const ITEM_HEIGHT_COLLAPSED = 60; // Height when collapsed
const ITEM_HEIGHT_EXPANDED = 200; // Estimated height when expanded (with scopes)
export const AccessManagementContainer = () => {
	const dispatch = useDispatch();
	const [activeTabIndex, setActiveTabIndex] = useState(0);
	const [expandedPrincipals, setExpandedPrincipals] = useState(new Set());
	const [expandedScopes, setExpandedScopes] = useState(new Set());
	const listRef = useRef(null);
	const itemHeightsRef = useRef(new Map()); // Track heights for each item
	const rowObserversRef = useRef(new Map());
	const [scopeState, setScopeState] = useState({});
	const scopeStateRef = useRef({});
	const [loginStatusState, setLoginStatusState] = useState({});
	const loginStatusStateRef = useRef({});
	const loginStatusRequestSeqRef = useRef(0);
	const loginStatusLatestRequestRef = useRef({});
	const [talentLmsSyncState, setTalentLmsSyncState] = useState({});
	const talentLmsSyncStateRef = useRef({});
	const talentLmsSyncRequestSeqRef = useRef(0);
	const talentLmsSyncLatestRequestRef = useRef({});
	const [reconcilingTalentLmsEmails, setReconcilingTalentLmsEmails] = useState({});
	const [approvingEmails, setApprovingEmails] = useState({});
	
	// Track how many items we've loaded for each tab/search combination
	const [loadedCounts, setLoadedCounts] = useState({
		users: PAGE_SIZE,
		projects: PAGE_SIZE,
		roles: PAGE_SIZE,
		searchUsers: PAGE_SIZE,
		searchProjects: PAGE_SIZE,
		searchRoles: PAGE_SIZE,
	});
	const [hasMoreByKey, setHasMoreByKey] = useState({
		users: true,
		projects: true,
		roles: true,
		searchUsers: true,
		searchProjects: true,
		searchRoles: true,
	});
	const [isNewUserOpen, setIsNewUserOpen] = useState(false);
	const [isDownloadingReport, setIsDownloadingReport] = useState(false);
	const [downloadReportError, setDownloadReportError] = useState('');
	const { checkUserHasPermissions } = usePermissions();
	const canViewAdmin = checkUserHasPermissions(permissions.CAN_VIEW_ADMIN);
	const [addProjectModalOpen, setAddProjectModalOpen] = useState(false);
	const [addProjectUser, setAddProjectUser] = useState(null);
	const [addRoleModalOpen, setAddRoleModalOpen] = useState(false);
	const [addRoleUser, setAddRoleUser] = useState(null);
	const [addUserToProjectModalOpen, setAddUserToProjectModalOpen] = useState(false);
	const [addUserToProjectProject, setAddUserToProjectProject] = useState(null);
	const [addUserToRoleModalOpen, setAddUserToRoleModalOpen] = useState(false);
	const [addUserToRoleRole, setAddUserToRoleRole] = useState(null);
	const [isEditUserOpen, setIsEditUserOpen] = useState(false);
	const [editUser, setEditUser] = useState(null);

	// Import query hooks for tab config
	const useUsersQueryHook = useUsersQuery;
	const useManagedProjectsQueryHook = useManagedProjectsQuery;
	const useRolesQueryHook = useRolesQuery;
	const useSearchUsersQueryHook = useSearchUsersQuery;
	const useSearchProjectsQueryHook = useSearchProjectsQuery;
	const useSearchRolesQueryHook = useSearchRolesQuery;

	// Mutations for adding/removing users from projects
	const [addUsersToProject, { isLoading: isAddingUsersToProject }] = useAddUsersToProjectMutation();
	const [addProjectsToUser, { isLoading: isAddingProjectsToUser }] = useAddProjectsToUserMutation();
	const [addRolesToUser, { isLoading: isAddingRolesToUser }] = useAddRolesToUserMutation();
	const [addUsersToRole, { isLoading: isAddingUsersToRole }] = useAddUsersToRoleMutation();
	const [addUser, { isLoading: isAddingUser, error: addUserError }] = useAddUserMutation();
	const [removeUserFromProject] = useRemoveUserFromProjectMutation();
	const [removeUserFromRole] = useRemoveUserFromRoleMutation();
	const [removeRolesFromUser, { isLoading: isRemovingRolesFromUser }] = useRemoveRolesFromUserMutation();
	const [approveUserSalesforce] = useApproveUserSalesforceMutation();
	const [triggerUserProjects] = useLazyUserProjectsQuery();
	const [triggerUserRoles] = useLazyUserRolesQuery();
	const [triggerUserLoginStatus] = useLazyUserLoginStatusQuery();
	const [triggerUserTalentLmsSync] = useLazyUserTalentLmsSyncQuery();
	const [reconcileUserTalentLms] = useReconcileUserTalentLmsMutation();
	const [triggerProjectUsers] = useLazyProjectUsersQuery();
	const [triggerRoleUsers] = useLazyRoleUsersQuery();

	// Handler functions
	const getUserEmail = (user) => user?.email || user?.Email || '';
	const invalidateTalentLmsSyncForEmail = (emailToClear) => {
		if (!emailToClear) return;
		talentLmsSyncStateRef.current = clearTalentLmsSyncEntry(talentLmsSyncStateRef.current, emailToClear);
		setTalentLmsSyncState((prev) => clearTalentLmsSyncEntry(prev, emailToClear));
	};
	const getUserFirstName = (user) => user?.firstName || user?.FirstName || '';
	const getUserLastName = (user) => user?.lastName || user?.LastName || '';
	const getUserDisplayName = (user) => {
		const fullName = `${getUserFirstName(user)} ${getUserLastName(user)}`.trim();
		return fullName || getUserEmail(user);
	};
	const getProjectId = (project) => project?.projectId || project?.ProjectId || project?.id;
	const getProjectCode = (project) => project?.projectCode || project?.ProjectCode || project?.code;
	const getProjectTitle = (project) => project?.projectTitle || project?.ProjectTitle || project?.description;
	const getRoleName = (role) => role?.name || role?.Name;
	const getExistingRoleNamesForUser = (user) => {
		if (!user) return [];
		const email = getUserEmail(user);
		const scopeRoles = scopeState[`${email}::roles`]?.items;
		const fallback = user.roles || user.Roles || [];
		const source = Array.isArray(scopeRoles) && scopeRoles.length > 0 ? scopeRoles : fallback;
		return source.map((role) => getRoleName(role)).filter(Boolean);
	};

	const updateScopeItems = useCallback((scopeId, updater) => {
		setScopeState((prev) => {
			if (!prev[scopeId]) {
				return prev;
			}
			const current = prev[scopeId] || { items: [], skip: 0, hasMore: true, loading: false, error: null };
			const nextItems = updater(current.items || []);
			const nextSkip = Math.max(current.skip, nextItems.length);
			return {
				...prev,
				[scopeId]: {
					...current,
					items: nextItems,
					skip: nextSkip,
				},
			};
		});
	}, []);
	const handleAddProject = (user) => {
		setAddProjectUser(user);
		setAddProjectModalOpen(true);
	};

	const handleRemoveProject = async (userId, projectId) => {
		try {
			await removeUserFromProject({
				projectId,
				email: userId,
				comment: `Removed project ${projectId} from user ${userId}`
			}).unwrap();
			updateScopeItems(`${userId}::projects`, (items) =>
				items.filter((item) => getProjectId(item) !== projectId)
			);
		} catch (error) {
			console.error('Failed to remove project from user:', error);
		}
	};

	const handleAddRole = (user) => {
		const email = getUserEmail(user);
		const scopeId = `${email}::roles`;
		// Eagerly load so existing roles are pre-selected when the modal opens
		loadScopeItems(scopeId, (skip, take) => fetchUserRoles(email, skip, take));
		setAddRoleUser(user);
		setAddRoleModalOpen(true);
	};

	const handleRemoveRole = async (userId, roleId) => {
		try {
			await removeUserFromRole({
				email: userId,
				roleName: roleId,
				comment: `Removed role ${roleId} from user ${userId}`
			}).unwrap();
			updateScopeItems(`${userId}::roles`, (items) =>
				items.filter((item) => getRoleName(item) !== roleId)
			);
			invalidateTalentLmsSyncForEmail(userId);
			loadTalentLmsSync(userId, { force: true });
		} catch (error) {
			console.error('Failed to remove role from user:', error);
		}
	};

	const handleAddUserToProject = (project) => {
		setAddUserToProjectProject(project);
		setAddUserToProjectModalOpen(true);
	};

	const handleRemoveUserFromProject = async (projectId, user) => {
		try {
			const email = getUserEmail(user);
			await removeUserFromProject({
				projectId,
				email,
				comment: `Removed user ${email} from project`
			}).unwrap();
			updateScopeItems(`${projectId}::users`, (items) =>
				items.filter((item) => getUserEmail(item) !== email)
			);
		} catch (error) {
			console.error('Failed to remove user from project:', error);
		}
	};

	const handleAddUserToRole = (role) => {
		setAddUserToRoleRole(role);
		setAddUserToRoleModalOpen(true);
	};

	const handleRemoveUserFromRole = async (roleId, userId) => {
		try {
			await removeUserFromRole({
				email: userId,
				roleName: roleId,
				comment: `Removed user ${userId} from role ${roleId}`
			}).unwrap();
			updateScopeItems(`${roleId}::users`, (items) =>
				items.filter((item) => getUserEmail(item) !== userId)
			);
			invalidateTalentLmsSyncForEmail(userId);
			loadTalentLmsSync(userId, { force: true });
		} catch (error) {
			console.error('Failed to remove user from role:', error);
		}
	};

	const handleNewUser = () => {
		setIsNewUserOpen(true);
	};

	const handleNewUserCancel = () => {
		setIsNewUserOpen(false);
	};

	const handleEditUser = (user) => {
		setEditUser(user);
		setIsEditUserOpen(true);
	};

	const handleEditUserCancel = () => {
		setIsEditUserOpen(false);
		setEditUser(null);
	};

	const handleDownloadReport = useCallback(async () => {
		setIsDownloadingReport(true);
		setDownloadReportError('');
		try {
			const token = await getAuth0Token();
			const url = urljoin(API_BASEPATH_PORTAL, 'admin', 'users', 'projects', 'report');
			const response = await fetch(url, {
				method: 'GET',
				headers: { authorization: `Bearer ${token}` },
			});
			if (!response.ok) {
				throw new Error('Failed to download report');
			}
			const blob = await response.blob();
			const contentDisposition = response.headers.get('content-disposition') || '';
			const filenameMatch = contentDisposition.match(/filename\*=(?:UTF-8'')?([^;]+)/i);
			const fallbackMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
			const rawFilename = filenameMatch?.[1] || fallbackMatch?.[1] || 'projects-report.xlsx';
			const filename = decodeURIComponent(rawFilename.trim());
			FileUtils.download(blob, filename, blob.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
		} catch (error) {
			setDownloadReportError('Failed to download report. Please try again.');
		} finally {
			setIsDownloadingReport(false);
		}
	}, []);

	const handleUpsertUser = async (payload, { comment, onSuccess, originalEmail }) => {
		const lookupEmail = originalEmail != null ? originalEmail : payload.email;
		try {
			const result = await addUser({ user: payload, comment, originalEmail }).unwrap();
			const warningMessages = Array.isArray(result?.warnings) && result.warnings.length > 0
				? result.warnings
				: result?.warning
					? [result.warning]
					: [];
			warningMessages.forEach((message) => toast.warn(message));
			const invalidateLoginStatus = (emailToClear) => {
				if (!emailToClear) return;
				loginStatusStateRef.current = clearLoginStatusEntry(loginStatusStateRef.current, emailToClear);
				setLoginStatusState((prev) => clearLoginStatusEntry(prev, emailToClear));
			};
			invalidateLoginStatus(lookupEmail);
			if (payload.email && payload.email !== lookupEmail) {
				invalidateLoginStatus(payload.email);
			}
			const invalidateTalentLmsSync = (emailToClear) => {
				if (!emailToClear) return;
				invalidateTalentLmsSyncForEmail(emailToClear);
			};
			invalidateTalentLmsSync(lookupEmail);
			if (payload.email && payload.email !== lookupEmail) {
				invalidateTalentLmsSync(payload.email);
			}
			loadTalentLmsSync(payload.email || lookupEmail, { force: true });
			dispatch(portalApi.util.updateQueryData('users', { skip: 0, take: PAGE_SIZE }, (draft) => {
				if (!Array.isArray(draft)) return;
				const existingIndex = draft.findIndex((user) => (user.email || user.Email) === lookupEmail);
				if (existingIndex >= 0) {
					draft[existingIndex] = { ...draft[existingIndex], ...payload };
					return;
				}

				draft.push(payload);
				draft.sort((left, right) => {
					const leftEmail = (left.email || left.Email || '').toLowerCase();
					const rightEmail = (right.email || right.Email || '').toLowerCase();
					return leftEmail.localeCompare(rightEmail);
				});
			}));
			const canUpdateSearch =
				currentTabConfig?.key === 'users' &&
				shouldSearchServer &&
				searchQuery.length > 0;
			if (canUpdateSearch) {
				const searchValue = searchQuery.toUpperCase();
				const matchesSearch = [
					payload.email,
					payload.firstName,
					payload.lastName
				].some((value) => (value || '').toUpperCase().startsWith(searchValue));

				if (matchesSearch) {
					dispatch(portalApi.util.updateQueryData(
						'searchUsers',
						{ searchText: searchQuery, skip: 0, take: PAGE_SIZE },
						(draft) => {
							if (!Array.isArray(draft)) return;
							const existingIndex = draft.findIndex((user) => (user.email || user.Email) === lookupEmail);
							if (existingIndex >= 0) {
								draft[existingIndex] = { ...draft[existingIndex], ...payload };
								return;
							}

							draft.push(payload);
							draft.sort((left, right) => {
								const leftEmail = (left.email || left.Email || '').toLowerCase();
								const rightEmail = (right.email || right.Email || '').toLowerCase();
								return leftEmail.localeCompare(rightEmail);
							});
						}
					));
				} else {
					// Ensure the all-users cache is up-to-date when clearing search
					usersQueryResult.refetch();
				}
			}
			onSuccess?.();
		} catch (error) {
			console.error('Failed to upsert user:', error);
		}
	};

	const handleNewUserSubmit = async (payload) => {
		await handleUpsertUser(payload, {
			comment: `Added user ${payload.email}`,
			onSuccess: () => setIsNewUserOpen(false),
		});
	};

	const handleEditUserSubmit = async (payload) => {
		const originalEmail = editUser ? getUserEmail(editUser) : undefined;
		await handleUpsertUser(payload, {
			comment: `Updated user ${payload.email}`,
			originalEmail,
			onSuccess: () => {
				setIsEditUserOpen(false);
				setEditUser(null);
			},
		});
	};

	const handleCloseAddProjectModal = () => {
		setAddProjectModalOpen(false);
		setAddProjectUser(null);
	};

	const handleCloseAddRoleModal = () => {
		setAddRoleModalOpen(false);
		setAddRoleUser(null);
	};

	const handleCloseAddUserToProjectModal = () => {
		setAddUserToProjectModalOpen(false);
		setAddUserToProjectProject(null);
	};

	const handleCloseAddUserToRoleModal = () => {
		setAddUserToRoleModalOpen(false);
		setAddUserToRoleRole(null);
	};

	const handleConfirmAddProjectToUser = async (projects) => {
		if (!addProjectUser || !Array.isArray(projects) || projects.length === 0) return;
		try {
			const email = getUserEmail(addProjectUser);
			const projectIds = projects.map((project) => getProjectId(project)).filter(Boolean);
			await addProjectsToUser({
				email,
				projectIds,
				user: {
					email,
					firstName: getUserFirstName(addProjectUser),
					lastName: getUserLastName(addProjectUser),
				},
				comment: `Added ${projectIds.length} projects to user ${email}`
			}).unwrap();

			updateScopeItems(`${email}::projects`, (items) => {
				const next = [
					...items,
					...projects.map((project) => ({
						projectId: getProjectId(project),
						projectCode: getProjectCode(project),
						projectTitle: getProjectTitle(project),
					}))
				];
				const unique = new Map(next.map((item) => [getProjectId(item), item]).filter(([key]) => key));
				return Array.from(unique.values()).sort((left, right) =>
					(getProjectCode(left) || '').localeCompare(getProjectCode(right) || '')
				);
			});

			handleCloseAddProjectModal();
		} catch (error) {
			console.error('Failed to add project to user:', error);
		}
	};

	const handleConfirmAddRoleToUser = async ({ selectedRoles, selectedRoleNames }) => {
		if (!addRoleUser) return;
		try {
			const email = getUserEmail(addRoleUser);
			const existingRoleNames = getExistingRoleNamesForUser(addRoleUser);
			const selectedNames = selectedRoleNames || selectedRoles.map((role) => getRoleName(role)).filter(Boolean);
			const rolesToAdd = selectedNames.filter((name) => !existingRoleNames.includes(name));
			const rolesToRemove = existingRoleNames.filter((name) => !selectedNames.includes(name));

			if (rolesToAdd.length > 0) {
				await addRolesToUser({
					email,
					roleNames: rolesToAdd,
					user: {
						email,
						firstName: getUserFirstName(addRoleUser),
						lastName: getUserLastName(addRoleUser),
					},
					comment: `Added ${rolesToAdd.length} roles to user ${email}`
				}).unwrap();
			}

			if (rolesToRemove.length > 0) {
				await removeRolesFromUser({
					email,
					roleNames: rolesToRemove,
					user: {
						email,
						firstName: getUserFirstName(addRoleUser),
						lastName: getUserLastName(addRoleUser),
					},
					comment: `Removed ${rolesToRemove.length} roles from user ${email}`
				}).unwrap();
			}

			const updatedRoles = Array.isArray(selectedRoles) ? selectedRoles : [];
			updateScopeItems(`${email}::roles`, () => {
				const unique = new Map(updatedRoles.map((role) => [getRoleName(role), role]).filter(([key]) => key));
				return Array.from(unique.values()).sort((left, right) =>
					(getRoleName(left) || '').localeCompare(getRoleName(right) || '')
				);
			});

			handleCloseAddRoleModal();
			invalidateTalentLmsSyncForEmail(email);
			loadTalentLmsSync(email, { force: true });
		} catch (error) {
			console.error('Failed to add role to user:', error);
		}
	};

	const handleConfirmAddUserToProject = async (users) => {
		if (!addUserToProjectProject || !Array.isArray(users) || users.length === 0) return;
		try {
			const projectId = getProjectId(addUserToProjectProject);
			const payloadUsers = users.map((user) => ({
				email: getUserEmail(user),
				firstName: getUserFirstName(user),
				lastName: getUserLastName(user),
			})).filter((user) => user.email);
			await addUsersToProject({
				projectId,
				users: payloadUsers,
				comment: `Added ${payloadUsers.length} users to project ${projectId}`
			}).unwrap();

			updateScopeItems(`${projectId}::users`, (items) => {
				const next = [
					...items,
					...payloadUsers.map((user) => ({
						id: user.email,
						email: user.email,
						name: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email
					}))
				];
				const unique = new Map(next.map((item) => [getUserEmail(item), item]).filter(([key]) => key));
				return Array.from(unique.values()).sort((left, right) =>
					(getUserEmail(left) || '').localeCompare(getUserEmail(right) || '')
				);
			});

			handleCloseAddUserToProjectModal();
		} catch (error) {
			console.error('Failed to add user to project:', error);
		}
	};

	const handleConfirmAddUserToRole = async (users) => {
		if (!addUserToRoleRole || !Array.isArray(users) || users.length === 0) return;
		try {
			const roleName = getRoleName(addUserToRoleRole);
			const payloadUsers = users.map((user) => ({
				email: getUserEmail(user),
				firstName: getUserFirstName(user),
				lastName: getUserLastName(user),
			})).filter((user) => user.email);
			await addUsersToRole({
				roleName,
				users: payloadUsers,
				comment: `Added ${payloadUsers.length} users to role ${roleName}`
			}).unwrap();

			updateScopeItems(`${roleName}::users`, (items) => {
				const next = [
					...items,
					...payloadUsers.map((user) => ({
						id: user.email,
						email: user.email,
						name: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email
					}))
				];
				const unique = new Map(next.map((item) => [getUserEmail(item), item]).filter(([key]) => key));
				return Array.from(unique.values()).sort((left, right) =>
					(getUserEmail(left) || '').localeCompare(getUserEmail(right) || '')
				);
			});

			handleCloseAddUserToRoleModal();
		} catch (error) {
			console.error('Failed to add user to role:', error);
		}
	};

	useEffect(() => {
		scopeStateRef.current = scopeState;
	}, [scopeState]);

	useEffect(() => {
		loginStatusStateRef.current = loginStatusState;
	}, [loginStatusState]);

	useEffect(() => {
		talentLmsSyncStateRef.current = talentLmsSyncState;
	}, [talentLmsSyncState]);

	const loadTalentLmsSync = useCallback(async (email, { force = false } = {}) => {
		const emailKey = normalizeTalentLmsSyncEmailKey(email);
		if (!emailKey) return;
		const current = talentLmsSyncStateRef.current[emailKey] || { loading: false, error: null, syncStatus: null };
		if (shouldSkipTalentLmsSyncFetch(current, { force })) return;

		const requestId = talentLmsSyncRequestSeqRef.current + 1;
		talentLmsSyncRequestSeqRef.current = requestId;
		talentLmsSyncLatestRequestRef.current[emailKey] = requestId;

		talentLmsSyncStateRef.current = {
			...talentLmsSyncStateRef.current,
			[emailKey]: { ...current, loading: true, error: null },
		};
		setTalentLmsSyncState((prev) => ({
			...prev,
			[emailKey]: { ...current, loading: true, error: null },
		}));

		try {
			const syncData = await triggerUserTalentLmsSync({ email: emailKey }).unwrap();
			if (talentLmsSyncLatestRequestRef.current[emailKey] !== requestId) return;
			const next = { loading: false, error: null, ...syncData };
			talentLmsSyncStateRef.current = { ...talentLmsSyncStateRef.current, [emailKey]: next };
			setTalentLmsSyncState((prev) => ({ ...prev, [emailKey]: next }));
		} catch (error) {
			if (talentLmsSyncLatestRequestRef.current[emailKey] !== requestId) return;
			const next = { loading: false, error: true, syncStatus: null, branches: [], lastSyncError: '' };
			talentLmsSyncStateRef.current = { ...talentLmsSyncStateRef.current, [emailKey]: next };
			setTalentLmsSyncState((prev) => ({ ...prev, [emailKey]: next }));
		}
	}, [triggerUserTalentLmsSync]);

	const handleReconcileTalentLms = useCallback(async (email) => {
		const emailKey = normalizeTalentLmsSyncEmailKey(email);
		if (!emailKey) return;
		setReconcilingTalentLmsEmails((prev) => ({ ...prev, [emailKey]: true }));
		try {
			const result = await reconcileUserTalentLms({ email: emailKey }).unwrap();
			(result?.warnings || []).forEach((message) => toast.warn(message));
			await loadTalentLmsSync(emailKey, { force: true });
		} catch (error) {
			toast.error(getProblemDetailMessage(error) || 'TalentLMS reconciliation failed.');
		} finally {
			setReconcilingTalentLmsEmails((prev) => {
				const next = { ...prev };
				delete next[emailKey];
				return next;
			});
		}
	}, [loadTalentLmsSync, reconcileUserTalentLms]);

	const loadLoginStatus = useCallback(async (email, { force = false } = {}) => {
		const emailKey = normalizeLoginStatusEmailKey(email);
		if (!emailKey) return;
		const current = loginStatusStateRef.current[emailKey] || { loading: false, error: null, status: null };
		if (shouldSkipLoginStatusFetch(current, { force })) return;

		const requestId = loginStatusRequestSeqRef.current + 1;
		loginStatusRequestSeqRef.current = requestId;
		loginStatusLatestRequestRef.current[emailKey] = requestId;

		loginStatusStateRef.current = {
			...loginStatusStateRef.current,
			[emailKey]: { ...current, loading: true, error: null },
		};
		setLoginStatusState((prev) => ({
			...prev,
			[emailKey]: { ...current, loading: true, error: null },
		}));

		try {
			const status = await triggerUserLoginStatus(
				{ email: emailKey },
				{ preferCacheValue: false },
			).unwrap();
			if (loginStatusLatestRequestRef.current[emailKey] !== requestId) return;
			const next = { loading: false, error: null, status };
			loginStatusStateRef.current = { ...loginStatusStateRef.current, [emailKey]: next };
			setLoginStatusState((prev) => ({ ...prev, [emailKey]: next }));
		} catch (error) {
			if (loginStatusLatestRequestRef.current[emailKey] !== requestId) return;
			const next = { loading: false, error: error || true, status: null };
			loginStatusStateRef.current = { ...loginStatusStateRef.current, [emailKey]: next };
			setLoginStatusState((prev) => ({ ...prev, [emailKey]: next }));
		}
	}, [triggerUserLoginStatus]);

	// Approve a user in Salesforce (Lead-only). On success, the endpoint returns the freshly resolved
	// login status, which we write straight into loginStatusState so the toggle reflects the new state.
	const handleToggleApproval = useCallback(async (email) => {
		const emailKey = normalizeLoginStatusEmailKey(email);
		if (!emailKey || approvingEmails[emailKey]) return;
		setApprovingEmails((prev) => ({ ...prev, [emailKey]: true }));
		try {
			const status = await approveUserSalesforce({ email: emailKey }).unwrap();
			const next = { loading: false, error: null, status };
			loginStatusStateRef.current = { ...loginStatusStateRef.current, [emailKey]: next };
			setLoginStatusState((prev) => ({ ...prev, [emailKey]: next }));
		} catch (error) {
			console.error('Failed to approve user in Salesforce:', error);
			toast.error(getProblemDetailMessage(error, 'Failed to approve user in Salesforce. Please try again.'));
		} finally {
			setApprovingEmails((prev) => ({ ...prev, [emailKey]: false }));
		}
	}, [approveUserSalesforce, approvingEmails]);

	const loadScopeItems = useCallback(async (scopeId, fetchItems, options = {}) => {
		const { append = false } = options;
		const current = scopeStateRef.current[scopeId] || { items: [], skip: 0, hasMore: true, loading: false, error: null };
		if (current.loading || (!current.hasMore && append)) return;
		if (!append && current.items.length > 0) return;
		// Mark as loading immediately to avoid duplicate calls while state updates
		scopeStateRef.current = {
			...scopeStateRef.current,
			[scopeId]: { ...current, loading: true, error: null },
		};
		const nextSkip = append ? current.skip : 0;
		setScopeState((prev) => ({
			...prev,
			[scopeId]: {
				...current,
				loading: true,
				error: null,
			},
		}));

		try {
			const items = await fetchItems(nextSkip, PAGE_SIZE);
			const normalizedItems = Array.isArray(items) ? items : [];
			const combinedItems = append ? [...current.items, ...normalizedItems] : normalizedItems;
			const hasMore = normalizedItems.length === PAGE_SIZE;

			setScopeState((prev) => ({
				...prev,
				[scopeId]: {
					items: combinedItems,
					skip: nextSkip + normalizedItems.length,
					hasMore,
					loading: false,
					error: null,
				},
			}));
		} catch (error) {
			setScopeState((prev) => ({
				...prev,
				[scopeId]: {
					...current,
					loading: false,
					error: error || true,
				},
			}));
		}
	}, []);

	const fetchUserProjects = useCallback(async (email, skip = 0, take = PAGE_SIZE) => {
		return triggerUserProjects({ email, skip, take }).unwrap();
	}, [triggerUserProjects]);

	const fetchUserRoles = useCallback(async (email, skip = 0, take = PAGE_SIZE) => {
		return triggerUserRoles({ email, skip, take }).unwrap();
	}, [triggerUserRoles]);

	const fetchProjectUsers = useCallback(async (projectId, skip = 0, take = PAGE_SIZE) => {
		return triggerProjectUsers({ projectId, skip, take }).unwrap();
	}, [triggerProjectUsers]);

	const fetchRoleUsers = useCallback(async (roleName, skip = 0, take = PAGE_SIZE) => {
		return triggerRoleUsers({ roleName, skip, take }).unwrap();
	}, [triggerRoleUsers]);

	// Create configuration with handlers, data sources, and query hooks
	const accessTabsConfig = useMemo(() => createAccessTabsConfig({
		handleAddProject,
		handleRemoveProject,
		handleAddRole,
		handleRemoveRole,
		handleAddUserToProject,
		handleRemoveUserFromProject,
		handleAddUserToRole,
		handleRemoveUserFromRole,
		fetchUserProjects,
		fetchUserRoles,
		fetchProjectUsers,
		fetchRoleUsers,
	}, {
		users: [],
		projects: [],
		roles: [],
	}, {
		useAllQuery: {
			useUsersQuery: useUsersQueryHook,
			useManagedProjectsQuery: useManagedProjectsQueryHook,
			useRolesQuery: useRolesQueryHook,
		},
		useSearchQuery: {
			useSearchUsersQuery: useSearchUsersQueryHook,
			useSearchProjectsQuery: useSearchProjectsQueryHook,
			useSearchRolesQuery: useSearchRolesQueryHook,
		},
	}), [
		useUsersQueryHook,
		useManagedProjectsQueryHook,
		useRolesQueryHook,
		useSearchUsersQueryHook,
		useSearchProjectsQueryHook,
		useSearchRolesQueryHook,
		fetchUserProjects,
		fetchUserRoles,
		fetchProjectUsers,
		fetchRoleUsers,
	]);

	const {
		searchText,
		setSearchText,
		handleSearchChange,
		handleSearchKeyUp,
		clearDebounce,
	} = useAccessSearch({ accessTabsConfig, activeTabIndex });

	const {
		currentTabConfig,
		shouldSearchServer,
		searchQuery,
		allItems,
		searchItems,
		refetchAll,
		refetchSearch,
		isSearching,
		isLoadingAll,
		isError,
		error,
		usersQueryResult,
	} = useAccessQueries({
		accessTabsConfig,
		activeTabIndex,
		searchText,
		pageSize: PAGE_SIZE,
		useUsersQueryHook,
		useManagedProjectsQueryHook,
		useRolesQueryHook,
		useSearchUsersQueryHook,
		useSearchProjectsQueryHook,
		useSearchRolesQueryHook,
	});

	// Load more items when scrolling near the end (generic implementation)
	const loadMoreItems = useCallback(async () => {
		if (!currentTabConfig || isLoadingMoreRef.current) {
			return;
		}

		isLoadingMoreRef.current = true;

		try {
			const minChars = currentTabConfig.search?.minChars || 0;
			const hasSearchText = searchText.length >= minChars;
			const tabKey = currentTabConfig.key;
			const countKey = `${hasSearchText ? 'search' : ''}${tabKey}`;
			const canLoadMore = hasMoreByKey[countKey] !== false;
			if (!canLoadMore) {
				return;
			}

			const currentLoaded = loadedCounts[countKey] || PAGE_SIZE;
			const nextSkip = currentLoaded;
			let result;
			const searchEndpointMap = {
				users: portalApi.endpoints.searchUsers,
				projects: portalApi.endpoints.searchProjects,
				roles: portalApi.endpoints.searchRoles,
			};
			const defaultEndpointMap = {
				users: portalApi.endpoints.users,
				projects: portalApi.endpoints.managedProjects,
				roles: portalApi.endpoints.roles,
			};
			const endpointMap = hasSearchText ? searchEndpointMap : defaultEndpointMap;
			const endpoint = endpointMap[tabKey];
			if (!endpoint) {
				return;
			}

			const queryArgs = hasSearchText
				? { searchText: searchQuery, skip: nextSkip, take: PAGE_SIZE }
				: { skip: nextSkip, take: PAGE_SIZE };

			result = await dispatch(endpoint.initiate(
				queryArgs,
				{ forceRefetch: true }
			));

			const mergedLength = Array.isArray(result?.data) ? result.data.length : currentLoaded;
			if (mergedLength <= currentLoaded) {
				setHasMoreByKey(prev => ({ ...prev, [countKey]: false }));
				return;
			}

			setLoadedCounts(prev => ({ ...prev, [countKey]: mergedLength }));
		} catch (error) {
			console.error('Failed to load more items:', error);
		} finally {
			isLoadingMoreRef.current = false;
		}
	}, [currentTabConfig, searchText, searchQuery, loadedCounts, hasMoreByKey, dispatch]);

	// Reset loaded counts when tab or search changes
	useEffect(() => {
		const tabKey = currentTabConfig?.key || '';
		const minChars = currentTabConfig?.search?.minChars || 0;
		const hasSearchText = searchText.length >= minChars;
		const countKey = `${hasSearchText ? 'search' : ''}${tabKey}`;
		
		// Reset the count for the current tab/search combination
		setLoadedCounts(prev => ({
			...prev,
			[countKey]: PAGE_SIZE
		}));
		setHasMoreByKey(prev => ({
			...prev,
			[countKey]: true
		}));
	}, [activeTabIndex, searchText, currentTabConfig]);

	// Filter principals based on search text using config (generic implementation)
	const filteredPrincipals = useMemo(() => {
		if (!currentTabConfig) return [];
		
		// Server-side filtering: use RTK Query results
		if (currentTabConfig.search?.mode === 'server') {
			const minChars = currentTabConfig.search.minChars || 0;
			const hasSearchText = searchText.length >= minChars;
			const mapItem = currentTabConfig.mapItem || ((item) => item);
			
			// Use all items when no search, search results when searching
			const sourceData = hasSearchText ? searchItems : allItems;
			
			// Map items using the tab's mapper function
			const mapped = Array.isArray(sourceData) ? sourceData.map(mapItem) : [];
			
			// Remove duplicates based on the principal ID to prevent duplicate entries from API bugs
			const seen = new Set();
			const unique = mapped.filter(item => {
				const id = currentTabConfig.getPrincipalId(item);
				if (seen.has(id)) {
					return false;
				}
				seen.add(id);
				return true;
			});
			
			return unique;
		}
		
		// Client-side filtering: apply only when search mode is "client"
		if (currentTabConfig.search?.mode === 'client') {
			const searchLower = searchText.toLowerCase();
			const data = currentTabConfig.getData();
			return data.filter(principal => 
				currentTabConfig.filterPrincipal(principal, searchLower)
			);
		}
		
		// Fallback: return all data (should not happen with proper configuration)
		return currentTabConfig.getData();
	}, [currentTabConfig, searchText, allItems, searchItems]);

	const handleTabChange = (event, newValue) => {
		clearDebounce();
		setActiveTabIndex(newValue);
		setExpandedPrincipals(new Set());
		setExpandedScopes(new Set());
		setSearchText('');
	};

	const togglePrincipal = (principal) => {
		const principalId = currentTabConfig.getPrincipalId(principal);
		const newExpanded = new Set(expandedPrincipals);
		if (newExpanded.has(principalId)) {
			newExpanded.delete(principalId);
			setExpandedScopes((prev) => {
				const next = new Set(prev);
				for (const key of next) {
					if (key.startsWith(`${principalId}::`)) {
						next.delete(key);
					}
				}
				return next;
			});
		} else {
			// Prefetch all scopes for this principal so scope expand is instant
			(currentTabConfig.scopes || []).forEach((scopeConfig) => {
				if (scopeConfig.fetchItems) {
					const scopeId = `${principalId}::${scopeConfig.key}`;
					loadScopeItems(scopeId, (skip, take) => scopeConfig.fetchItems(principal, skip, take));
				}
			});
			if (canViewAdmin && currentTabConfig.key === 'users') {
				loadLoginStatus(getUserEmail(principal));
				loadTalentLmsSync(getUserEmail(principal));
			}
			newExpanded.add(principalId);
		}
		setExpandedPrincipals(newExpanded);
	};

	// Generic scope rendering function (replaces renderUserScopes, renderProjectScopes, renderRoleScopes)
	const renderScopes = (principal, scopesConfig, principalId) => {
		return scopesConfig.map((scopeConfig, scopeIndex) => {
			const scopeId = `${principalId}::${scopeConfig.key}`;
			const isScopeExpanded = expandedScopes.has(scopeId);
			const state = scopeState[scopeId] || { items: [], skip: 0, hasMore: true, loading: false, error: null };
			const items = scopeConfig.fetchItems
				? state.items
				: scopeConfig.getItems(principal);
			const isLastScope = scopeIndex === scopesConfig.length - 1;
			const isScopeLoading = scopeConfig.fetchItems && state.loading;
			const isScopeError = scopeConfig.fetchItems && state.error;

			return (
				<Box key={scopeConfig.key} sx={isLastScope ? {} : { mb: 2 }}>
					<ListItem
						button
						onClick={() => {
							setExpandedScopes((prev) => {
								const next = new Set(prev);
								if (next.has(scopeId)) {
									next.delete(scopeId);
								} else {
								if (scopeConfig.fetchItems) {
									loadScopeItems(scopeId, (skip, take) => scopeConfig.fetchItems(principal, skip, take));
								}
									next.add(scopeId);
								}
								return next;
							});
						}}
						sx={{ px: 0 }}
					>
						<IconButton size="small" sx={{ mr: 1 }}>
							{isScopeExpanded ? <ExpandMore /> : <ChevronRight />}
						</IconButton>
						<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
							{scopeConfig.label}
						</Typography>
					</ListItem>
					<Collapse in={isScopeExpanded} timeout="auto" unmountOnExit>
						<List dense sx={{ ml: 3 }}>
							{isScopeLoading ? (
								<ListItem>
									<ListItemText primary={`Loading ${scopeConfig.label.toLowerCase()}...`} />
								</ListItem>
							) : null}
							{!isScopeLoading && isScopeError ? (
								<ListItem>
									<ListItemText primary={`Failed to load ${scopeConfig.label.toLowerCase()}`} />
									<Button
										size="small"
										onClick={() => loadScopeItems(scopeId, (skip, take) => scopeConfig.fetchItems(principal, skip, take))}
									>
										Retry
									</Button>
								</ListItem>
							) : null}
							{!isScopeLoading && !isScopeError && items.length === 0 ? (
								<ListItem>
									<ListItemText primary={`No ${scopeConfig.label.toLowerCase()} found`} />
								</ListItem>
							) : null}
							{!isScopeLoading && !isScopeError && items.map((item) => (
								<ListItem
									key={scopeConfig.getItemKey(item)}
									secondaryAction={
										<IconButton
											edge="end"
											size="small"
											color="error"
											onClick={() => scopeConfig.onRemove(principal, item)}
										>
											<Close fontSize="small" />
										</IconButton>
									}
								>
									<ListItemText 
										primary={scopeConfig.getItemText(item)}
										secondary={scopeConfig.getItemSecondaryText ? scopeConfig.getItemSecondaryText(item) : null}
									/>
								</ListItem>
							))}
						</List>
						{scopeConfig.fetchItems && !isScopeError && state.hasMore ? (
							<Button
								size="small"
								onClick={() => loadScopeItems(scopeId, (skip, take) => scopeConfig.fetchItems(principal, skip, take), { append: true })}
								sx={{ mt: 1, ml: 3 }}
								disabled={state.loading}
							>
								{state.loading ? 'Loading...' : 'Load more'}
							</Button>
						) : null}
						<Button
							size="small"
							startIcon={<Add />}
							onClick={() => scopeConfig.onAdd(principal)}
							sx={{ mt: 0.5, ml: 3 }}
						>
							{scopeConfig.addLabel}
						</Button>
					</Collapse>
				</Box>
			);
		});
	};

	// Get item height for virtual scrolling (variable based on expanded state)
	const getItemSize = useCallback((index) => {
		if (!currentTabConfig || index >= filteredPrincipals.length) return ITEM_HEIGHT_COLLAPSED;
		
		const principal = filteredPrincipals[index];
		const principalId = currentTabConfig.getPrincipalId(principal);
		const isExpanded = expandedPrincipals.has(principalId);
		
		// Use cached height if available, otherwise estimate
		const cachedHeight = itemHeightsRef.current.get(principalId);
		if (cachedHeight) return cachedHeight;
		
		// Estimate height based on expanded state
		return isExpanded ? ITEM_HEIGHT_EXPANDED : ITEM_HEIGHT_COLLAPSED;
	}, [currentTabConfig, filteredPrincipals, expandedPrincipals]);

	// Update item height when expanded state changes
	useEffect(() => {
		if (listRef.current) {
			// Reset cached heights and recalculate
			itemHeightsRef.current.clear();
			listRef.current.resetAfterIndex(0);
		}
	}, [expandedPrincipals, filteredPrincipals]);

	// Render a single principal item for virtual scrolling
	const renderPrincipalItem = useCallback(({ index, style }) => {
		if (!currentTabConfig || index >= filteredPrincipals.length) return null;
		
		const principal = filteredPrincipals[index];
					const principalId = currentTabConfig.getPrincipalId(principal);
					const isExpanded = expandedPrincipals.has(principalId);
					const primaryText = currentTabConfig.getPrincipalPrimaryText(principal);
					const secondaryText = currentTabConfig.getPrincipalSecondaryText(principal);
					const scopes = renderScopes(principal, currentTabConfig.scopes, principalId);
		const canEditUser = currentTabConfig.key === 'users';
		const userEmail = getUserEmail(principal);
		const emailKey = normalizeLoginStatusEmailKey(userEmail);
		const loginStatusEntry = canViewAdmin && canEditUser
			? (loginStatusState[emailKey] || { loading: false, error: null, status: null })
			: null;
		const talentLmsSyncEntry = canViewAdmin && canEditUser
			? (talentLmsSyncState[emailKey] || { loading: false, error: null, syncStatus: null, branches: [] })
			: null;
		const branchSummary = formatTalentLmsBranchSummary(talentLmsSyncEntry?.branches || []);

		const setRowRef = (node) => {
			const existingObserver = rowObserversRef.current.get(principalId);
			if (!node) {
				if (existingObserver) {
					existingObserver.disconnect();
					rowObserversRef.current.delete(principalId);
				}
				return;
			}

			const updateHeight = (target) => {
				const height = target.getBoundingClientRect().height;
				const cachedHeight = itemHeightsRef.current.get(principalId);
				if (cachedHeight !== height) {
					itemHeightsRef.current.set(principalId, height);
					if (listRef.current) {
						listRef.current.resetAfterIndex(index);
					}
				}
			};

			updateHeight(node);

			if (!existingObserver) {
				const observer = new ResizeObserver((entries) => {
					for (const entry of entries) {
						updateHeight(entry.target);
					}
				});
				observer.observe(node);
				rowObserversRef.current.set(principalId, observer);
			}
		};

					return (
			<div style={style}>
				<div ref={setRowRef}>
					<Paper sx={{ mb: 1.5, p: 1, mx: 1 }}>
							<ListItem
								button
								onClick={() => togglePrincipal(principal)}
								sx={{ px: 1 }}
								secondaryAction={canEditUser ? (
									<IconButton
										edge="end"
										aria-label="edit user"
										sx={{ color: 'primary.main' }}
										onClick={(event) => {
											event.stopPropagation();
											handleEditUser(principal);
										}}
									>
										<ModeEditOutline fontSize="small" />
									</IconButton>
								) : null}
							>
								<IconButton
									size="small"
									sx={{ mr: 1 }}
									onClick={(e) => {
										e.stopPropagation();
								togglePrincipal(principal);
									}}
								>
									{isExpanded ? <ExpandMore /> : <ChevronRight />}
								</IconButton>
								<ListItemText
									primary={primaryText}
									secondary={secondaryText}
								/>
							</ListItem>
							<Collapse in={isExpanded} timeout="auto" unmountOnExit>
								<Box sx={{ pl: 6, pr: 2, pb: 2 }}>
									{loginStatusEntry?.loading ? (
										<Typography variant="body2" sx={{ mb: 2 }}>
											Loading login status...
										</Typography>
									) : null}
									{loginStatusEntry && !loginStatusEntry.loading && loginStatusEntry.error ? (
										<Box sx={{ mb: 2 }}>
											<Typography variant="body2" color="error">
												Failed to load login status
											</Typography>
											<Button size="small" onClick={() => loadLoginStatus(userEmail, { force: true })}>
												Retry
											</Button>
										</Box>
									) : null}
									{loginStatusEntry && !loginStatusEntry.loading && !loginStatusEntry.error && loginStatusEntry.status ? (
										<Typography variant="body2" sx={{ mb: 2, fontWeight: 600 }}>
											Login Status:{' '}
											<Box
												component="span"
												sx={{ color: getLoginStatusTypographyColor(loginStatusEntry.status) }}
											>
												{loginStatusEntry.status}
											</Box>
										</Typography>
									) : null}
									{loginStatusEntry && !loginStatusEntry.loading && !loginStatusEntry.error && loginStatusEntry.status
										&& loginStatusEntry.status !== 'Employee' && loginStatusEntry.status !== 'Legacy' ? (() => {
											const isApproved = loginStatusEntry.status === 'Contact'
												|| loginStatusEntry.status === 'Approved Lead';
											const isApproving = !!approvingEmails[emailKey];
											return (
												<Typography
													variant="body2"
													sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}
												>
													Salesforce Approved:{' '}
													<Switch
														checked={isApproved}
														disabled={isApproved || isApproving}
														onChange={() => handleToggleApproval(userEmail)}
														size="small"
													/>
												</Typography>
											);
										})() : null}
									{talentLmsSyncEntry?.loading ? (
										<Typography variant="body2" sx={{ mb: 2 }}>
											Loading TalentLMS sync status...
										</Typography>
									) : null}
									{talentLmsSyncEntry && !talentLmsSyncEntry.loading && talentLmsSyncEntry.error ? (
										<Box sx={{ mb: 2 }}>
											<Typography variant="body2" color="error">
												Failed to load TalentLMS sync status
											</Typography>
											<Button size="small" onClick={() => loadTalentLmsSync(userEmail, { force: true })}>
												Retry
											</Button>
										</Box>
									) : null}
									{talentLmsSyncEntry && !talentLmsSyncEntry.loading && !talentLmsSyncEntry.error && talentLmsSyncEntry.syncStatus ? (
										<Box sx={{ mb: 2 }}>
											<Typography variant="body2" sx={{ fontWeight: 600 }}>
												Learning Center Branches:{' '}
												{branchSummary.length > 0 ? branchSummary.join(', ') : 'None assigned'}
											</Typography>
											<Typography variant="body2" sx={{ fontWeight: 600 }}>
												TalentLMS Sync:{' '}
												<Box
													component="span"
													sx={{ color: getTalentLmsSyncTypographyColor(talentLmsSyncEntry.syncStatus) }}
												>
													{talentLmsSyncEntry.syncStatus}
												</Box>
											</Typography>
											{talentLmsSyncEntry.lastSyncSuccessUtc ? (
												<Typography variant="caption" display="block">
													Last successful sync: {talentLmsSyncEntry.lastSyncSuccessUtc}
												</Typography>
											) : null}
											{talentLmsSyncEntry.lastSyncError ? (
												<Typography variant="caption" color="error" display="block">
													Last sync error: {talentLmsSyncEntry.lastSyncError}
												</Typography>
											) : null}
											<Button
												size="small"
												sx={{ mt: 1 }}
												disabled={!!reconcilingTalentLmsEmails[emailKey]}
												onClick={() => handleReconcileTalentLms(userEmail)}
											>
												{reconcilingTalentLmsEmails[emailKey] ? 'Reconciling...' : 'Reconcile TalentLMS'}
											</Button>
										</Box>
									) : null}
									{scopes}
								</Box>
							</Collapse>
						</Paper>
				</div>
			</div>
		);
	}, [currentTabConfig, filteredPrincipals, expandedPrincipals, togglePrincipal, renderScopes, handleEditUser, canViewAdmin, loginStatusState, loadLoginStatus, approvingEmails, handleToggleApproval, talentLmsSyncState, loadTalentLmsSync, reconcilingTalentLmsEmails, handleReconcileTalentLms]);

	// Track if we're currently loading to prevent duplicate requests
	const isLoadingMoreRef = useRef(false);

	// Handle items rendered to detect when we need to load more
	const handleItemsRendered = useCallback(({ visibleStartIndex, visibleStopIndex, overscanStartIndex, overscanStopIndex }) => {
		if (!currentTabConfig || isLoadingMoreRef.current) {
			return;
		}
		
		// Load more when we're within 20 items of the end
		const threshold = 20;
		const minChars = currentTabConfig.search?.minChars || 0;
		const hasSearchText = searchText.length >= minChars;
		const tabKey = currentTabConfig.key;
		const countKey = `${hasSearchText ? 'search' : ''}${tabKey}`;
		const currentLoaded = loadedCounts[countKey] || PAGE_SIZE;
		const canLoadMore = hasMoreByKey[countKey] !== false;
		if (!canLoadMore) {
			return;
		}
			
		if (visibleStopIndex >= filteredPrincipals.length - threshold) {
			const shouldLoad = filteredPrincipals.length > 0 && 
				filteredPrincipals.length >= currentLoaded - threshold;
			
			if (shouldLoad && !isLoadingMoreRef.current) {
				loadMoreItems();
			}
		}
	}, [currentTabConfig, filteredPrincipals.length, searchText, loadedCounts, hasMoreByKey, loadMoreItems]);

	// Calculate list height dynamically
	const [listHeight, setListHeight] = useState(600);
	
	useEffect(() => {
		const updateHeight = () => {
			if (typeof window !== 'undefined') {
				setListHeight(window.innerHeight - 300);
			}
		};
		
		updateHeight();
		window.addEventListener('resize', updateHeight);
		return () => window.removeEventListener('resize', updateHeight);
	}, []);

	const renderPrincipalList = () => {
		if (!currentTabConfig) return null;

		const minChars = currentTabConfig.search?.minChars || 0;
		const hasSearchText = searchText.length >= minChars;
		const isLoading = hasSearchText ? isSearching : isLoadingAll;
		
		if (isError) {
			return (
				<Box sx={{ textAlign: 'center', py: 4 }}>
					<Typography variant="body2" color="error">
						Failed to load {currentTabConfig.emptyStateLabel}
					</Typography>
					<Button
						size="small"
						onClick={() => (hasSearchText ? refetchSearch() : refetchAll())}
						sx={{ mt: 1 }}
					>
						Retry
					</Button>
				</Box>
			);
		}

		if (isLoading && filteredPrincipals.length === 0) {
			return (
				<Box sx={{ textAlign: 'center', py: 4 }}>
					<Typography variant="body2" color="text.secondary">
						Loading {currentTabConfig.emptyStateLabel}...
					</Typography>
				</Box>
			);
		}

		if (filteredPrincipals.length === 0) {
			return (
				<Box sx={{ textAlign: 'center', py: 4 }}>
					<Typography variant="body2" color="text.secondary">
						No {currentTabConfig.emptyStateLabel} found
					</Typography>
				</Box>
			);
		}

		return (
			<Box sx={{ height: listHeight, width: '100%' }}>
				<VariableSizeList
					ref={listRef}
					height={listHeight}
					itemCount={filteredPrincipals.length}
					itemSize={getItemSize}
					width="100%"
					onItemsRendered={handleItemsRendered}
					overscanCount={5} // Render 5 extra items above/below viewport
				>
					{renderPrincipalItem}
				</VariableSizeList>
			</Box>
		);
	};

	const minChars = currentTabConfig?.search?.minChars || 0;
	const hasSearchText = searchText.length >= minChars;
	const showSearchLoading = hasSearchText ? isSearching : isLoadingAll;

	return (
		<Box sx={{ p: 3, width: '100%', margin: '0 auto' }}>
			<Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 3 }}>
				<Tabs value={activeTabIndex} onChange={handleTabChange}>
					{accessTabsConfig.map((tabConfig, index) => (
						<Tab key={tabConfig.key} label={tabConfig.label} />
					))}
				</Tabs>
			</Box>

			<Box sx={{ mb: 3, width: '100%', display: 'flex', alignItems: 'center', gap: 1 }} className="AccessManagementSearch">
				<SearchBox
					iconColor="tertiary"
					showResults={false}
					hintText={currentTabConfig?.search?.placeholder || 'Search'}
					autoComplete="off"
					name="access-management-search"
					disabled={isNewUserOpen || isEditUserOpen}
					onChange={handleSearchChange}
					onKeyUp={handleSearchKeyUp}
					handleCancel={() => handleSearchKeyUp('')}
					mapData={{ primaryText: 'name', value: 'id' }}
					dataSource={[]}
				/>
				{showSearchLoading ? <CircularProgress size={20} /> : null}
			</Box>

			{renderPrincipalList()}
			<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', mt: 2, gap: 1 }}>
				<Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
					{canViewAdmin ? (
						<Button
							variant="outlined"
							startIcon={<Download />}
							onClick={handleDownloadReport}
							disabled={isDownloadingReport}
						>
							Download Report
						</Button>
					) : null}
					<Button
						variant="contained"
						startIcon={<Add />}
						onClick={handleNewUser}
					>
						New User
					</Button>
				</Box>
				{downloadReportError ? (
					<Typography variant="body2" color="error">
						{downloadReportError}
					</Typography>
				) : null}
			</Box>
			<Modal
				open={isNewUserOpen}
				onClose={handleNewUserCancel}
				title="Add New User"
			>
				<AddNewUserModal
					open={isNewUserOpen}
					onCancel={handleNewUserCancel}
					onSubmit={handleNewUserSubmit}
					isSubmitting={isAddingUser}
					errorMessage={addUserError ? getProblemDetailMessage(addUserError, 'Failed to add user. Please try again.') : ''}
				/>
			</Modal>
			<Modal
				open={isEditUserOpen}
				onClose={handleEditUserCancel}
				title="Edit User"
			>
				<AddNewUserModal
					open={isEditUserOpen}
					onCancel={handleEditUserCancel}
					onSubmit={handleEditUserSubmit}
					isSubmitting={isAddingUser}
					errorMessage={addUserError ? getProblemDetailMessage(addUserError, 'Failed to edit user. Please try again.') : ''}
					initialValues={{
						firstName: getUserFirstName(editUser),
						lastName: getUserLastName(editUser),
						email: getUserEmail(editUser),
					}}
					isEdit
					submitLabel="Edit User"
				/>
			</Modal>
			<Modal
				open={isDownloadingReport}
				onClose={() => {}}
				title="Downloading Report"
				hideCloseButton
				disableEscapeKeyDown
			>
				<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, minWidth: 300 }}>
					<CircularProgress />
					<Typography variant="body2">Please wait while we generate your report.</Typography>
				</Box>
			</Modal>
			<Modal
				open={addProjectModalOpen}
				onClose={handleCloseAddProjectModal}
				title={`Add Project${addProjectUser ? ` to ${getUserDisplayName(addProjectUser)}` : ''}`}
			>
				<AddProjectToUserModal
					open={addProjectModalOpen}
					onCancel={handleCloseAddProjectModal}
					onConfirm={handleConfirmAddProjectToUser}
					isSubmitting={isAddingProjectsToUser}
					pageSize={PAGE_SIZE}
					user={addProjectUser}
					getProjectId={getProjectId}
					getProjectCode={getProjectCode}
					getProjectTitle={getProjectTitle}
					existingProjectIds={
						addProjectUser
							? (scopeState[`${getUserEmail(addProjectUser)}::projects`]?.items || [])
								.map((item) => getProjectId(item))
								.filter(Boolean)
							: []
					}
				/>
			</Modal>
			<Modal
				open={addRoleModalOpen}
				onClose={handleCloseAddRoleModal}
				title={`Manage Roles${addRoleUser ? ` for ${getUserDisplayName(addRoleUser)}` : ''}`}
			>
				<AddRoleToUserModal
					open={addRoleModalOpen}
					onCancel={handleCloseAddRoleModal}
					onConfirm={handleConfirmAddRoleToUser}
					isSubmitting={isAddingRolesToUser || isRemovingRolesFromUser}
					getRoleName={getRoleName}
					existingRoleNames={
						addRoleUser
							? getExistingRoleNamesForUser(addRoleUser)
							: []
					}
				/>
			</Modal>
			<Modal
				open={addUserToProjectModalOpen}
				onClose={handleCloseAddUserToProjectModal}
				title={`Add User${addUserToProjectProject ? ` to ${getProjectCode(addUserToProjectProject)}` : ''}`}
			>
				<AddUserToProjectModal
					open={addUserToProjectModalOpen}
					onCancel={handleCloseAddUserToProjectModal}
					onConfirm={handleConfirmAddUserToProject}
					isSubmitting={isAddingUsersToProject}
					pageSize={PAGE_SIZE}
					getUserEmail={getUserEmail}
					getUserFirstName={getUserFirstName}
					getUserLastName={getUserLastName}
					existingUserEmails={
						addUserToProjectProject
							? (scopeState[`${getProjectId(addUserToProjectProject)}::users`]?.items || [])
								.map((item) => getUserEmail(item))
								.filter(Boolean)
							: []
					}
				/>
			</Modal>
			<Modal
				open={addUserToRoleModalOpen}
				onClose={handleCloseAddUserToRoleModal}
				title={`Add User${addUserToRoleRole ? ` to ${getRoleName(addUserToRoleRole)}` : ''}`}
			>
				<AddUserToRoleModal
					open={addUserToRoleModalOpen}
					onCancel={handleCloseAddUserToRoleModal}
					onConfirm={handleConfirmAddUserToRole}
					isSubmitting={isAddingUsersToRole}
					pageSize={PAGE_SIZE}
					getUserEmail={getUserEmail}
					getUserFirstName={getUserFirstName}
					getUserLastName={getUserLastName}
					existingUserEmails={
						addUserToRoleRole
							? (scopeState[`${getRoleName(addUserToRoleRole)}::users`]?.items || [])
								.map((item) => getUserEmail(item))
								.filter(Boolean)
							: []
					}
				/>
			</Modal>
		</Box>
	);
};

export default AccessManagementContainer;
